import { z } from 'zod';
/** Mirrors Prisma `SsoIdpProtocol` (lock-step test in packages/db/src/sso-schema.test.ts). */
export const SSO_IDP_PROTOCOLS = ['SAML', 'OIDC'];
/** Mirrors Prisma `SsoIdpPreset`. */
export const SSO_IDP_PRESETS = ['OKTA', 'GENERIC'];
/** The API spelling: 'saml' | 'oidc', 'okta' | 'generic'. */
export const SSO_IDP_PROTOCOL_SLUGS = ['saml', 'oidc'];
export const ssoIdpProtocolSlugSchema = z.enum(SSO_IDP_PROTOCOL_SLUGS);
export const SSO_IDP_PRESET_SLUGS = ['okta', 'generic'];
export const ssoIdpPresetSlugSchema = z.enum(SSO_IDP_PRESET_SLUGS);
export const SSO_IDP_DISPLAY_NAME_MAX = 40;
/** Pasted or fetched SAML metadata above this is refused. */
export const SSO_SAML_METADATA_MAX_BYTES = 262_144;
/** At most this many signing certificates are kept and shown. */
export const SSO_SAML_MAX_CERTIFICATES = 10;
/** https only, at most 2048 chars, trimmed. */
const httpsUrlSchema = z
    .string()
    .trim()
    .max(2048)
    .url()
    .refine((value) => value.toLowerCase().startsWith('https://'), { message: 'must be an https URL' });
const displayNameSchema = z.string().trim().min(1).max(SSO_IDP_DISPLAY_NAME_MAX);
/** `PUT /api/web/tenant/sso/enterprise/identity-provider` — SAML (URL or pasted XML) or OIDC. */
export const configureEnterpriseIdpSchema = z
    .discriminatedUnion('protocol', [
    z
        .object({
        protocol: z.literal('saml'),
        preset: ssoIdpPresetSlugSchema,
        displayName: displayNameSchema,
        metadataUrl: httpsUrlSchema.optional(),
        metadataXml: z.string().min(1).max(SSO_SAML_METADATA_MAX_BYTES).optional(),
    })
        .strict(),
    z
        .object({
        protocol: z.literal('oidc'),
        preset: ssoIdpPresetSlugSchema,
        displayName: displayNameSchema,
        issuer: httpsUrlSchema,
        clientId: z.string().trim().min(1).max(255),
        /** SECRET (S7): write-only; stored with encryptSecret, never echoed. */
        clientSecret: z.string().min(1).max(1024),
    })
        .strict(),
])
    .superRefine((value, ctx) => {
    if (value.protocol !== 'saml')
        return;
    if ((value.metadataUrl === undefined) === (value.metadataXml === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['metadataUrl'],
            message: 'give exactly one of metadataUrl or metadataXml',
        });
    }
});
export const ssoIdentityProviderDTOSchema = z
    .object({
    protocol: ssoIdpProtocolSlugSchema,
    preset: ssoIdpPresetSlugSchema,
    displayName: z.string(),
    configuredAt: z.string().datetime(),
    saml: z
        .object({
        entityId: z.string(),
        ssoUrl: z.string(),
        metadataUrl: z.string().nullable(),
        certificateFingerprints: z.array(z.string()).max(SSO_SAML_MAX_CERTIFICATES),
        certificatesExpireAt: z.string().datetime(),
    })
        .strict()
        .nullable(),
    oidc: z
        .object({ issuer: z.string(), clientId: z.string(), clientSecretSet: z.literal(true) })
        .strict()
        .nullable(),
})
    .strict();
/** What the admin pastes into their IdP. Per environment, never per tenant (ADR 0042 decision 43). */
export const enterpriseServiceProviderDTOSchema = z
    .object({
    samlEntityId: z.string(),
    samlAcsUrl: z.string(),
    oidcRedirectUri: z.string(),
    /** The SAML attribute (and OIDC claim) the email must arrive in. */
    samlEmailAttribute: z.literal('email'),
})
    .strict();
//# sourceMappingURL=sso-enterprise.js.map